import * as ko from 'knockout'
import {myTripChecklistApi, myTripsApi, publicApi, TemplateDto, TripDto, UserTemplateDto} from '../../api/api-wrapper'
import {Context, Router} from '@profiscience/knockout-contrib-router';
import {config} from '../../globals';
import globalState from '../../global-state';
import {autobind} from "knockout-decorators";
import {postbox} from "../../components/util/postbox";
import i18nextko from "../../components/util/i18nko";
import {createConfirmModal} from "../../components/elements/modal/modal";

class ViewModelContext extends Context {
    templates: TemplateDto[];
    userTemplates: UserTemplateDto[];
    trip: TripDto;
}

class ViewModel {
    public pageTitle: KnockoutObservable<string>;
    public templates: KnockoutObservableArray<TemplateDto>;
    public userTemplates: KnockoutObservableArray<UserTemplateDto>;
    public config: config.IConfig = config;
    trip: TripDto;

    constructor(ctx: ViewModelContext) {
        this.pageTitle = ko.observable('templates module');
        this.templates = ko.observableArray(ctx.templates);
        this.userTemplates = ko.observableArray(ctx.userTemplates);

        // a trip already exists (from Reise App)
        if (ctx.trip) {
            this.trip = ctx.trip;
        }
    }

    public individual() {
        if (this.trip) {
            return Router.update(`/neu/individuell/${this.trip.id}`, {
                force: false,
                push: false
            });
        } else {
            return Router.update('/neu/individuell', {
                force: false,
                push: false
            });
        }
    }

    @autobind
    public selectTemplate(template: TemplateDto): Promise<boolean> {
        globalState.loading(true);
        if (this.trip) {
            // there is a trip already from the reise app -> create a checklist from template and go to checklist page
            // directly as there is no need to set the destination
            return myTripChecklistApi.setTripPredefinedChecklist(this.trip.id.toString(), template.id, {})
                .then(value => {
                    globalState.loading(false);
                    // add trip to local trips
                    globalState.trips.unshift(this.trip);

                    return Router.update(`/reise/${this.trip.id}/checkliste`, {
                        push: false,
                        with: {
                            showSettingsHint: true
                        }
                    });
                })
                .catch(err => {
                    globalState.loading(false);
                    console.error('failed to create checklist', err);
                    postbox.addError('groups.errors.failedToCreateChecklist');

                    return false;
                });

        } else {
            return myTripsApi.createPredefinedTrip(template.id, {})
                .then(value => value.data.tripId.toString())
                .then(value => {
                    globalState.loading(false);

                    return Router.update(`/neu/vorlagen/${value}`, {
                        force: false,
                        push: false,
                        with: {
                            imagePath: template.picture ?
                                `${config.templateImagePath}/${template.picture}` : config.defaultImage,
                            templateName: template.name,
                            ignoreRequireLogin: true,
                            userTemplate: false
                        }
                    });
                })
                .catch(err => {
                    globalState.loading(false);
                    console.error('failed to create checklist', err);
                    postbox.addError('groups.errors.failedToCreateChecklist');

                    return false;
                });
        }
    };

    @autobind
    public selectUserTemplate(userTemplate: UserTemplateDto): Promise<boolean> {
        globalState.loading(true);

        const userdefinedTripPromise = this.trip ?
            myTripChecklistApi.setTripUserdefinedChecklist(this.trip.id.toString(), userTemplate.tripId.toString(),
                {}) :
            myTripsApi.createUserdefinedTrip(userTemplate.tripId.toString(), {});

        return userdefinedTripPromise
            .then(response => response.data.tripId.toString())
            .then(value => {
                globalState.loading(false);

                return Router.update(`/neu/vorlagen/${value}`, {
                    force: false,
                    push: false,
                    with: {
                        imagePath: userTemplate.imageUrl ? userTemplate.imageUrl : config.defaultImage,
                        templateName: userTemplate.name,
                        ignoreRequireLogin: false,
                        userTemplate: true
                    }
                });
            })
            .catch(err => {
                globalState.loading(false);
                console.error('failed to create checklist', err);
                postbox.addError('groups.errors.failedToCreateChecklist');

                return false;
            });
    }

    @autobind
    public deleteUserTemplate(userTemplate: UserTemplateDto): Promise<any> {
        globalState.loading(true);

        return myTripsApi.deleteUserTemplate(userTemplate.tripId.toString())
            .then(() =>
                myTripsApi.getUserTemplates().then(result => this.userTemplates(result.data)))
            .then(() => {
                globalState.loading(false);
                postbox.clearErrors();
                postbox.addSuccess('templates.successes.deleteTemplate', {title: userTemplate.name});
            })
            .catch(err => {
                console.debug("failed to delete user defined template", userTemplate, err);
                globalState.loading(false);
                postbox.addError('templates.errors.failedToDeleteTemplate', {title: userTemplate.name});
            });
    }

    @autobind
    public deleteUserTemplateConfirmation(userTemplate: UserTemplateDto) {
        const title = i18nextko.t("templates.delete.confirmTitle");
        const text = i18nextko.t("templates.delete.confirmText");
        const confirm = i18nextko.t("global.delete");
        const cancel = i18nextko.t("global.cancel");
        const iconCssClass = "fo-icon fo-icon-trash";

        return createConfirmModal(text, title, confirm, cancel, iconCssClass)
            .then(() => {
                return this.deleteUserTemplate(userTemplate);
            })
            .catch(() => {
                return false;
            });
    }


    public templateImage(template: TemplateDto): string {
        return template.picture ?
            `${config.imagePath}/${config.templateImagePath}/${template.picture}` :
            config.defaultImage;
    }

    public userTemplateImage(userTemplate: UserTemplateDto): string {
        return userTemplate.imageUrl ?
            `${config.imagePath}/${userTemplate.imageUrl}` :
            config.defaultImage;
    }
}

export default <KnockoutLazyPageDefinition>{
    viewModel: ViewModel,
    template: require('./templates.html'),
    componentName: "templates",
    loader: (ctx: ViewModelContext) => {
        return Promise.all([
            publicApi.getTemplates().then(result => ctx.templates = result.data),
            globalState.isLoggedIn() ?
                myTripsApi.getUserTemplates().then(result => ctx.userTemplates = result.data) :
                Promise.resolve([])
        ]);
    }
};
